import { GAME_CONFIG, getStageForElapsedTime } from '../data/gameConfig';
import { getChordRoot, getNoteVoice, noteSoundKey } from '../data/music';
import { SongClock } from './SongClock';
import { StemPlayer } from './StemPlayer';
import { songBeatPhase } from '../data/shared/song';

export class AudioEngine {
  constructor() {
    this.ctx = null;
    this.lookahead = 25;
    this.scheduleAheadTime = 0.1;
    this.timerID = null;
    this.currentStreak = 0;
    this.speedMultiplier = 1;
    this.stageTimelines = {};
    this.voices = new Set();
    this.clock = new SongClock();
    this.chartByTick = new Map();
    this.noteVoices = new Map();
    this.acceptedNotes = new Set();
    this.holdVoices = new Map();
    this.lastMissAt = -Infinity;
    this.lastHoldCompleteAt = -Infinity;
    this.isPlaying = false;
    this.isPaused = false;
    this.songStartTime = 0;
    this.nextStep = 0;
    this.pausedSongTime = 0;
    this.stems = new StemPlayer();
    this.noteTimeline = [];
    this.nextChartEvent = 0;
  }

  async init() {
    if (!this.ctx) {
      const Context = window.AudioContext || window.webkitAudioContext;
      if (!Context) throw new Error('Este navegador no admite audio para el juego.');
      this.ctx = new Context({ latencyHint: 'interactive' });
      this.master = this.ctx.createDynamicsCompressor();
      this.master.threshold.value = -8;
      this.master.knee.value = 12;
      this.master.ratio.value = 4;
      this.master.connect(this.ctx.destination);
      this.music = this.ctx.createGain();
      this.music.gain.value = 0.72;
      this.music.connect(this.master);
      this.effects = this.ctx.createGain();
      this.effects.gain.value = 0.65;
      this.effects.connect(this.master);
      this.noiseBuffer = this.ctx.createBuffer(1, this.ctx.sampleRate, this.ctx.sampleRate);
      const output = this.noiseBuffer.getChannelData(0);
      for (let index = 0; index < output.length; index += 1) output[index] = Math.random() * 2 - 1;
    }
    if (this.ctx.state !== 'running') await this.ctx.resume();
  }

  get beatInterval() { return 60 / (GAME_CONFIG.bpm * this.speedMultiplier); }
  get countInterval() { return GAME_CONFIG.rhythm.songLeadSeconds / this.speedMultiplier / GAME_CONFIG.rhythm.countInBeats; }
  setStreak(seconds) { this.currentStreak = seconds; }
  setSpeed(speed) { this.speedMultiplier = speed; }
  setTimeline(timelines) { this.stageTimelines = timelines ?? {}; }

  setNoteTimeline(timeline) {
    this.chartByTick.clear();
    this.noteTimeline = timeline;
    timeline.forEach((event) => this.chartByTick.set(event.tick, event));
  }

  prepareSong() { return this.stems.load(); }
  getBeatPhase(time) { return songBeatPhase(time, this.speedMultiplier); }

  getSongTime(inputTimestamp) {
    if (!this.ctx || this.isPaused) return this.pausedSongTime;
    const now = performance.now();
    const time = this.clock.sample(this.ctx, this.songStartTime, now);
    const inputDelay = inputTimestamp > 0 && inputTimestamp <= now && now - inputTimestamp < 1000
      ? (now - inputTimestamp) / 1000 : 0;
    return time - inputDelay;
  }

  track(source, nodes, onEnded) {
    const voice = { source, nodes };
    this.voices.add(voice);
    source.onended = () => {
      source.disconnect();
      nodes.forEach((node) => node.disconnect());
      this.voices.delete(voice);
      onEnded?.();
    };
  }

  playSynth(freq, type, time, duration, volume = 0.1, bus = this.music) {
    if (!this.ctx) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    // A short attack avoids clicks without delaying the perceived strike.
    gain.gain.setValueAtTime(0.0001, time);
    gain.gain.linearRampToValueAtTime(volume, time + 0.003);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + duration);
    osc.connect(gain);
    gain.connect(bus);
    this.track(osc, [gain]);
    osc.start(time);
    osc.stop(time + duration);
  }

  playKick(time) {
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.frequency.setValueAtTime(150, time);
    osc.frequency.exponentialRampToValueAtTime(42, time + 0.12);
    gain.gain.setValueAtTime(0.65, time);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + 0.18);
    osc.connect(gain);
    gain.connect(this.music);
    this.track(osc, [gain]);
    osc.start(time);
    osc.stop(time + 0.18);
  }

  playNoise(time, duration, volume, frequency) {
    const noise = this.ctx.createBufferSource();
    const filter = this.ctx.createBiquadFilter();
    const gain = this.ctx.createGain();
    noise.buffer = this.noiseBuffer;
    filter.type = 'highpass';
    filter.frequency.value = frequency;
    gain.gain.setValueAtTime(volume, time);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + duration);
    noise.connect(filter);
    filter.connect(gain);
    gain.connect(this.music);
    this.track(noise, [filter, gain]);
    noise.start(time);
    noise.stop(time + duration);
  }

  scheduleGuide(event, lane, time) {
    if (!this.ctx || this.isPaused) return;
    const key = noteSoundKey(event.tick, lane);
    const { frequency, brightness } = getNoteVoice(event.tick, lane);
    const osc = this.ctx.createOscillator();
    const filter = this.ctx.createBiquadFilter();
    const envelope = this.ctx.createGain();
    const expression = this.ctx.createGain();
    const duration = Math.max(0.55, this.beatInterval * 1.5);
    osc.type = 'triangle';
    osc.frequency.value = frequency;
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(brightness, time);
    filter.frequency.exponentialRampToValueAtTime(frequency * 1.2, time + duration);
    envelope.gain.setValueAtTime(0.0001, time);
    envelope.gain.linearRampToValueAtTime(1, time + 0.004);
    envelope.gain.exponentialRampToValueAtTime(0.0001, time + duration);
    expression.gain.value = this.acceptedNotes.has(key) ? GAME_CONFIG.rhythm.hitVolume : GAME_CONFIG.rhythm.guideVolume;
    osc.connect(filter);
    filter.connect(envelope);
    envelope.connect(expression);
    expression.connect(this.effects);
    this.noteVoices.set(key, { expression, time, end: time + duration });
    this.acceptedNotes.delete(key);
    this.track(osc, [filter, envelope, expression], () => this.noteVoices.delete(key));
    osc.start(time);
    osc.stop(time + duration);
  }

  playHit(lane, judgement, note) {
    if (!this.ctx || this.isPaused || !note) return;
    const key = noteSoundKey(note.tick, lane);
    if (this.stems.buffers.size) {
      const event = this.chartByTick.get(note.tick);
      const when = this.songStartTime + note.targetHitTime;
      if (when > this.ctx.currentTime + this.scheduleAheadTime) this.acceptedNotes.add(key);
      else {
        // Retain early acceptance until the scheduler has visited this note.
        if (when > this.ctx.currentTime && note.tick >= this.nextChartEvent) this.acceptedNotes.add(key);
        this.stems.accent(event?.stem, when, true, this.ctx.currentTime);
      }
      if (note.holdDuration > 0) this.startHold(lane, note);
      return;
    }
    const voice = this.noteVoices.get(key);
    if (voice) {
      // The attack has already been scheduled on the beat. A late input
      // opens its existing tail, without generating a second, late attack.
      if (this.ctx.currentTime < voice.time) {
        voice.expression.gain.setValueAtTime(GAME_CONFIG.rhythm.hitVolume, voice.time);
      } else {
        voice.expression.gain.setTargetAtTime(GAME_CONFIG.rhythm.hitVolume, this.ctx.currentTime, 0.018);
      }
    } else if (this.songStartTime + note.targetHitTime > this.ctx.currentTime) {
      this.acceptedNotes.add(key);
    }
    if (note.holdDuration > 0) this.startHold(lane, note);
  }

  startHold(lane, note) {
    if (!this.ctx || this.isPaused || this.holdVoices.has(lane)) return;
    const time = Math.max(this.ctx.currentTime, this.songStartTime + note.targetHitTime);
    const end = this.songStartTime + note.targetHitTime + note.holdDuration;
    if (end <= time) return;
    if (this.stems.buffers.size) {
      // The recorded mix is not the legacy synth chord progression. Use a quiet,
      // unpitched texture, with no extra tempo grid or competing melody.
      const voice = this.createFeedbackTexture(time, end - time, 1100, 1800, 0.045, true, () => {
        if (this.holdVoices.get(lane) === voice) this.holdVoices.delete(lane);
      });
      this.holdVoices.set(lane, voice);
      return;
    }
    const osc = this.ctx.createOscillator();
    const harmony = this.ctx.createOscillator();
    const filter = this.ctx.createBiquadFilter();
    const gain = this.ctx.createGain();
    const voice = { gain, sources: [osc, harmony], end };
    const interval = this.beatInterval / 4;
    osc.type = 'triangle';
    harmony.type = 'sine';
    filter.type = 'lowpass';
    filter.frequency.value = 1300;
    // Follow chord changes even when a hold crosses a bar boundary.
    const startTick = Math.max(note.tick, Math.floor((time - this.songStartTime) / interval));
    const setChord = (tick, at) => {
      const root = getChordRoot(tick);
      osc.frequency.setTargetAtTime(root * 2, at, 0.025);
      harmony.frequency.setTargetAtTime(root * 3, at, 0.025);
    };
    setChord(startTick, time);
    for (let tick = Math.ceil((startTick + 1) / 16) * 16; this.songStartTime + tick * interval < end; tick += 16) {
      setChord(tick, this.songStartTime + tick * interval);
    }
    gain.gain.setValueAtTime(0.0001, time);
    gain.gain.linearRampToValueAtTime(0.065, Math.min(time + 0.035, end));
    // Gentle rhythmic swells, already scheduled on the musical grid.
    for (let tick = Math.ceil((startTick + 1) / 4) * 4; this.songStartTime + tick * interval + 0.08 < end; tick += 4) {
      const beatTime = this.songStartTime + tick * interval;
      gain.gain.linearRampToValueAtTime(0.045, beatTime);
      gain.gain.linearRampToValueAtTime(0.065, beatTime + 0.06);
    }
    gain.gain.linearRampToValueAtTime(0.0001, end + 0.06);
    osc.connect(filter);
    harmony.connect(filter);
    filter.connect(gain);
    gain.connect(this.effects);
    this.holdVoices.set(lane, voice);
    this.track(harmony, []);
    this.track(osc, [filter, gain], () => {
      if (this.holdVoices.get(lane) === voice) this.holdVoices.delete(lane);
    });
    voice.sources.forEach((source) => { source.start(time); source.stop(end + 0.08); });
  }

  stopHold(lane) {
    const voice = this.holdVoices.get(lane);
    if (!voice || !this.ctx) return;
    const time = this.ctx.currentTime;
    try {
      voice.gain.gain.cancelScheduledValues(time);
      voice.gain.gain.setTargetAtTime(0.0001, time, 0.012);
    } catch (_) { /* el nodo ya terminó: basta con soltar la voz */ }
    voice.sources.forEach((source) => {
      try { source.stop(time + 0.06); } catch (_) { /* ya detenido */ }
    });
    this.holdVoices.delete(lane);
  }

  playMiss() {
    if (!this.ctx || this.isPaused || this.ctx.currentTime - this.lastMissAt < 0.1) return;
    const time = this.ctx.currentTime;
    this.lastMissAt = time;
    // Zumbido grave disonante: corta la mezcla masterizada donde el ruido solo se camuflaba.
    this.playErrorBuzz(time);
    this.createFeedbackTexture(time, 0.22, 1500, 300, 0.38);
    // Dip breve de la música para que el error lea incluso con el compresor trabajando.
    const baseMusic = this.music.gain.value || 0.72;
    this.music.gain.setTargetAtTime(baseMusic * 0.5, time, 0.012);
    this.music.gain.setTargetAtTime(baseMusic, time + 0.14, 0.09);
  }

  playErrorBuzz(time) {
    const duration = 0.28;
    const gain = this.ctx.createGain();
    const oscA = this.ctx.createOscillator();
    const oscB = this.ctx.createOscillator();
    oscA.type = 'square';
    oscB.type = 'square';
    // Quinta baja desafinada: el batimento la hace inconfundible frente a la música.
    oscA.frequency.setValueAtTime(138, time);
    oscB.frequency.setValueAtTime(146, time);
    oscA.frequency.exponentialRampToValueAtTime(92, time + duration);
    oscB.frequency.exponentialRampToValueAtTime(98, time + duration);
    gain.gain.setValueAtTime(0.0001, time);
    gain.gain.linearRampToValueAtTime(0.42, time + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + duration);
    oscA.connect(gain);
    oscB.connect(gain);
    gain.connect(this.effects);
    this.track(oscA, []);
    this.track(oscB, [gain]);
    oscA.start(time);
    oscA.stop(time + duration + 0.02);
    oscB.start(time);
    oscB.stop(time + duration + 0.02);
  }

  playHoldComplete() {
    if (!this.ctx || this.isPaused || this.ctx.currentTime - this.lastHoldCompleteAt < 0.08) return;
    const time = this.ctx.currentTime;
    this.lastHoldCompleteAt = time;
    this.createFeedbackTexture(time, 0.2, 1500, 2900, 0.085);
  }

  createFeedbackTexture(time, duration, from, to, volume, loop = false, onEnded) {
    const source = this.ctx.createBufferSource();
    const filter = this.ctx.createBiquadFilter();
    const gain = this.ctx.createGain();
    source.buffer = this.noiseBuffer;
    source.loop = loop;
    filter.type = 'bandpass';
    filter.Q.value = 0.7;
    filter.frequency.setValueAtTime(from, time);
    filter.frequency.exponentialRampToValueAtTime(to, time + duration);
    gain.gain.setValueAtTime(0.0001, time);
    gain.gain.linearRampToValueAtTime(volume, time + Math.min(0.025, duration * 0.2));
    if (loop) gain.gain.setValueAtTime(volume, time + duration);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + duration + (loop ? 0.04 : 0));
    source.connect(filter);
    filter.connect(gain);
    gain.connect(this.effects);
    this.track(source, [filter, gain], onEnded);
    source.start(time);
    source.stop(time + duration + 0.05);
    return { gain, sources: [source], end: time + duration };
  }

  scheduleNote(step, time) {
    if (step < 0) {
      if (step % 4 === 0) this.playSynth(step === -16 ? 1046.5 : 783.99, 'sine', time, 0.06, 0.25, this.effects);
      return;
    }
    // Select the stage for the scheduled beat, never for the last rendered frame.
    const chartEvent = this.chartByTick.get(step);
    if (this.stems.buffers.size) {
      if (chartEvent) {
        const keys = chartEvent.lanes.map(lane => noteSoundKey(step, lane));
        this.stems.accent(chartEvent.stem, time, keys.some(key => this.acceptedNotes.has(key)), this.ctx.currentTime);
        keys.forEach(key => this.acceptedNotes.delete(key));
      }
      return;
    }
    if (chartEvent) chartEvent.lanes.forEach((lane) => this.scheduleGuide(chartEvent, lane, time));
    const stage = getStageForElapsedTime(step * this.beatInterval / 4);
    const events = this.stageTimelines[stage.id]?.[step % 64] ?? [];
    events.forEach((event) => {
      if (event.minStreak && this.currentStreak < event.minStreak) return;
      if (event.action === 'kick') this.playKick(time);
      else if (event.action === 'snare') this.playNoise(time, 0.12, 0.24, 1200);
      else if (event.action === 'hat') this.playNoise(time, 0.035, 0.075, 6500);
      else if (event.action === 'synth') this.playSynth(event.freq, event.wave, time, event.dur, event.vol);
    });
  }

  scheduler() {
    if (!this.isPlaying || this.isPaused) return;
    const interval = this.beatInterval / 4;
    if (this.noteTimeline.length) {
      const countStep = this.countInterval / 4;
      // Count-in is a fixed preparation pulse. Recorded attacks use absolute
      // source timestamps, including every tempo change in the recording.
      while (this.nextStep < 0 && this.songStartTime + this.nextStep * countStep < this.ctx.currentTime + this.scheduleAheadTime) {
        const time = this.songStartTime + this.nextStep * countStep;
        if (time >= this.ctx.currentTime - 0.02) this.scheduleNote(this.nextStep, Math.max(time, this.ctx.currentTime));
        this.nextStep++;
      }
      while (this.nextChartEvent < this.noteTimeline.length) {
        const event = this.noteTimeline[this.nextChartEvent];
        const time = this.songStartTime + event.targetHitTime;
        if (time >= this.ctx.currentTime + this.scheduleAheadTime) break;
        if (time >= this.ctx.currentTime - 0.02) this.scheduleNote(event.tick, Math.max(time, this.ctx.currentTime));
        else event.lanes.forEach(lane => this.acceptedNotes.delete(noteSoundKey(event.tick, lane)));
        this.nextChartEvent++;
      }
      this.timerID = setTimeout(() => this.scheduler(), this.lookahead);
      return;
    }
    // After a delayed timer, skip expired steps instead of playing a burst of
    // stale notes. Computing each timestamp from its tick prevents drift.
    const firstLiveStep = Math.ceil((this.ctx.currentTime - this.songStartTime - 0.02) / interval);
    this.nextStep = Math.max(this.nextStep, firstLiveStep);
    let nextTime = this.songStartTime + this.nextStep * interval;
    while (nextTime < this.ctx.currentTime + this.scheduleAheadTime) {
      if (this.nextStep * interval >= GAME_CONFIG.time.maxDuration) break;
      this.scheduleNote(this.nextStep, Math.max(this.ctx.currentTime, nextTime));
      this.nextStep += 1;
      nextTime = this.songStartTime + this.nextStep * interval;
    }
    this.timerID = setTimeout(() => this.scheduler(), this.lookahead);
  }

  startBGM() {
    this.stopBGM();
    this.songStartTime = this.ctx.currentTime + GAME_CONFIG.rhythm.startLeadSeconds
      + GAME_CONFIG.rhythm.songLeadSeconds / this.speedMultiplier;
    this.clock.reset();
    this.nextStep = -GAME_CONFIG.rhythm.countInBeats * 4;
    this.nextChartEvent = 0;
    this.stems.start(this.ctx, this.music, this.songStartTime, this.speedMultiplier);
    this.isPlaying = true;
    this.isPaused = false;
    this.scheduler();
  }

  async pause() {
    if (!this.ctx || this.isPaused) return;
    this.pausedSongTime = this.getSongTime();
    this.holdVoices.forEach((_, lane) => this.stopHold(lane));
    this.isPaused = true;
    clearTimeout(this.timerID);
    await this.ctx.suspend();
  }

  async prepareResume() {
    // Clear scheduled sounds before unlocking the device on the user gesture.
    // The song remains paused while the server acknowledges the transition.
    this.stopBGM();
    await this.init();
  }

  async resume() {
    await this.ctx.resume();
    if (!this.isPlaying) {
      this.songStartTime = this.ctx.currentTime - this.pausedSongTime;
      this.nextStep = Math.ceil(this.pausedSongTime / (this.countInterval / 4));
      this.nextChartEvent = this.noteTimeline.findIndex(event => event.targetHitTime >= this.pausedSongTime);
      if (this.nextChartEvent < 0) this.nextChartEvent = this.noteTimeline.length;
      this.stems.start(this.ctx, this.music, this.songStartTime, this.speedMultiplier, this.pausedSongTime);
      this.isPlaying = true;
    }
    this.clock.resume(this.songStartTime, this.pausedSongTime, performance.now());
    this.isPaused = false;
    this.scheduler();
  }

  stopBGM() {
    this.stems.stop();
    this.isPlaying = false;
    clearTimeout(this.timerID);
    this.voices.forEach(({ source, nodes }) => {
      source.onended = null;
      try { source.stop(); } catch (_) { /* ya terminó */ }
      try { source.disconnect(); } catch (_) { /* ya desconectado */ }
      nodes.forEach((node) => { try { node.disconnect(); } catch (_) { /* ya desconectado */ } });
    });
    this.voices.clear();
    this.noteVoices.clear();
    this.acceptedNotes.clear();
    this.holdVoices.clear();
    this.lastMissAt = -Infinity;
    this.lastHoldCompleteAt = -Infinity;
  }

  dispose() {
    this.stopBGM();
    this.stems.dispose();
    this.music?.disconnect();
    this.effects?.disconnect();
    this.master?.disconnect();
    if (this.ctx && this.ctx.state !== 'closed') this.ctx.close().catch(() => {});
    this.ctx = null;
  }
}
